import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Brain, ArrowLeft, AlertCircle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppCard, AppCardContent, AppButton,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import AIQuestionForm from "./AIQuestionForm";

/* ── Converted to Tailwind utilities in Phase 5 (2026-08-24) ──────────────────
 * AddEditAIQuestionPage.scss is deleted (TOKEN_CONTRACT §6.1). Three things came out
 * of it rather than across with it:
 *
 * 1. **It had its own shimmer.** `.ai-edit-page__skeleton` was a three-stop gradient
 *    plus a private `@keyframes ai-edit-shimmer`, i.e. a second implementation of
 *    `Common/Shimmer` — which Phase 6.5 made the app's loading state. It is the
 *    shared component now, so the keyframe is gone and this screen inherits any fix
 *    the real one gets.
 * 2. **The error alert had a dark-mode fork it did not need.** Two rgba(239,68,68,…)
 *    tints and a #b91c1c ink, restated in a `[data-theme="dark"]` block with a
 *    lighter ink. `--danger-soft` / `--danger-fg` already invert; both blocks
 *    collapse to one set of utilities.
 * 3. The tip bullets were `#3b82f6` — the v1 blue, in a list about categories.
 */
const TIP = "tw-relative tw-pl-4 tw-text-[0.82rem] tw-text-content-muted tw-leading-[1.5] " +
  "[&>strong]:tw-text-content [&>strong]:tw-font-semibold " +
  // The bullet is a pseudo-element because the <li>s are prose, not a component.
  "before:tw-content-[''] before:tw-absolute before:tw-left-0 before:tw-top-[0.55em] " +
  "before:tw-w-[5px] before:tw-h-[5px] before:tw-rounded-full before:tw-bg-primary";

const AddEditAIQuestionPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({ question: "", techStack: "", difficulty: "easy" });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    apiClient
      .get(`/ai/ai-questions/${id}`)
      .then((res) => {
        setForm({
          question: res.data?.question || "",
          techStack: res.data?.techStack || "",
          difficulty: res.data?.difficulty || "easy",
        });
      })
      .catch(() => setErr("Failed to load question."))
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.question.trim() || !form.techStack) {
      setErr("Please fill in the question and select a category.");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await apiClient.put(`/ai/ai-questions/${id}`, form);
        setToast({ show: true, message: "Question updated", type: "success" });
      } else {
        await apiClient.post("/ai/ai-questions", form);
        setToast({ show: true, message: "Question created", type: "success" });
      }
      // Tiny delay so the toast registers before navigation.
      setTimeout(() => navigate("/ai-bank"), 350);
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Failed to save question.");
    }
    setLoading(false);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <PageHeader
        icon={Brain}
        backButton
        onBack={() => navigate("/ai-bank")}
        title={isEdit ? "Edit AI Question" : "New AI Question"}
        description={
          isEdit
            ? "Update an existing AI interview question."
            : "Add a new AI interview question to your bank."
        }
        actions={
          <AppButton variant="outline" onClick={() => navigate("/ai-bank")}>
            <ArrowLeft size={16} /> Back
          </AppButton>
        }
      />

      <div className="tw-grid tw-grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)] tw-gap-5 max-[992px]:tw-grid-cols-1">
        <AppCard className="tw-m-0">
          <AppCardContent>
            {err && (
              <div
                className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-rounded-sm tw-bg-danger-soft tw-border tw-border-danger/25 tw-text-danger-fg tw-text-[0.85rem] tw-mb-4"
                role="alert"
              >
                <AlertCircle size={16} />
                <span>{err}</span>
              </div>
            )}

            {fetching ? (
              <div className="tw-flex tw-flex-col tw-gap-3">
                <Shimmer height={110} />
                <Shimmer height={38} count={2} />
              </div>
            ) : (
              <AIQuestionForm
                form={form}
                setForm={setForm}
                loading={loading}
                onSubmit={handleSubmit}
                isEdit={isEdit}
              />
            )}
          </AppCardContent>
        </AppCard>

        <aside className="tw-flex tw-flex-col tw-gap-4">
          <AppCard>
            <AppCardContent>
              <h4 className="tw-text-sm tw-font-semibold tw-text-content tw-mt-0 tw-mb-2 tw-uppercase tw-tracking-[0.05em]">Tips</h4>
              <ul className="tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-2">
                <li className={TIP}>Pick a focused category — students filter by it during interviews.</li>
                <li className={TIP}>Use the <strong>Manage categories</strong> link to pre-create new categories.</li>
                <li className={TIP}>Keep difficulty consistent: easy = warm-up, hard = senior-level.</li>
                <li className={TIP}>Need many questions? Use <strong>Import</strong> on the Questions list.</li>
              </ul>
            </AppCardContent>
          </AppCard>
        </aside>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AddEditAIQuestionPage;
